import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Calendar, MapPin, Clock, ArrowRight, Users, Ticket, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { upcomingEvents } from '../data/mock';

const EventsPage = () => {
  const location = useLocation();
  const [festivalVolunteer, setFestivalVolunteer] = useState({
    name: '',
    email: '',
    phone: '',
    shift: '',
    tshirtSize: '',
    notes: '',
    agreement: false
  });

  const shiftOptions = [
    '4:00 PM - 6:00 PM (Setup & Early Service)',
    '6:00 PM - 8:00 PM (Peak Service)',
    '7:00 PM - 9:00 PM (Late Service & Breakdown)'
  ];

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  const handleFestivalVolunteerSubmit = (e) => {
    e.preventDefault();
    if (!festivalVolunteer.agreement) {
      toast.error('Please confirm you can work the full 2-hour shift.');
      return;
    }
    toast.success("Mahalo! Your volunteer application has been received. We'll be in touch with shift details and your 2 free tickets!");
    setFestivalVolunteer({
      name: '',
      email: '',
      phone: '',
      shift: '',
      tshirtSize: '',
      notes: '',
      agreement: false
    });
  };

  const pastEvents = [
    {
      id: 101,
      title: 'Spring Cancer Awareness Walk',
      date: 'April 15, 2025',
      location: 'Kapiolani Park',
      attendees: 250,
      raised: '$15,000'
    },
    {
      id: 102,
      title: 'Caregiver Appreciation Luncheon',
      date: 'March 10, 2025',
      location: 'Hawaiian Hilton Village',
      attendees: 85,
      raised: '$5,000'
    },
    {
      id: 103,
      title: 'Nutrition Workshop Series',
      date: 'February 2025',
      location: 'Online & In-Person',
      attendees: 120,
      raised: '$2,500'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-[#5D4E6D] py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E07A5F]/20 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Events</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6 leading-tight">
              Join Our Community Events
            </h1>
            <p className="text-gray-200 text-lg leading-relaxed">
              From fundraising walks to educational workshops, our events bring the community together 
              to support cancer patients and their families across Hawaii.
            </p>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-24 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Mark Your Calendar</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4">
              Upcoming Events
            </h2>
          </div>

          <div className="space-y-8">
            {upcomingEvents.map((event) => (
              <Card
                key={event.id}
                className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className="grid md:grid-cols-3">
                  <div className="aspect-square md:aspect-auto overflow-hidden">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="md:col-span-2 p-8">
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                      <Badge className="bg-[#E07A5F]/10 text-[#E07A5F] hover:bg-[#E07A5F]/20">
                        {event.type}
                      </Badge>
                    </div>
                    <h3 className="text-2xl font-bold text-[#2D2A32] mb-4">{event.title}</h3>
                    <p className="text-gray-600 mb-6">{event.description}</p>
                    <div className="grid sm:grid-cols-3 gap-4 mb-6">
                      <div className="flex items-center text-gray-600">
                        <Calendar className="w-5 h-5 mr-2 text-[#5D4E6D]" />
                        {event.date}
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Clock className="w-5 h-5 mr-2 text-[#5D4E6D]" />
                        {event.time}
                      </div>
                      <div className="flex items-center text-gray-600">
                        <MapPin className="w-5 h-5 mr-2 text-[#5D4E6D]" />
                        {event.location}
                      </div>
                    </div>

                    {event.qrCode && (
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 p-5 mb-6 bg-[#F5F0EB] rounded-2xl border border-[#E07A5F]/20">
                        <div className="w-32 h-32 flex-shrink-0 bg-white p-2 rounded-xl shadow-md">
                          <img
                            src={event.qrCode}
                            alt="Scan QR code for tickets"
                            className="w-full h-full object-contain"
                            data-testid={`event-qr-${event.id}`}
                          />
                        </div>
                        <div>
                          <p className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider mb-1">
                            Tickets On Sale Now
                          </p>
                          <h4 className="text-xl font-bold text-[#2D2A32] mb-2">
                            Scan to Get Your Tickets
                          </h4>
                          <p className="text-gray-600 text-sm">
                            Scan the QR code with your phone camera, or tap the button below to purchase tickets online.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row flex-wrap gap-4">
                      {event.link && (
                        <Button
                          asChild
                          data-testid={`event-tickets-btn-${event.id}`}
                          className="bg-[#E07A5F] hover:bg-[#C96A50] text-white rounded-full px-6 transition-all duration-300"
                        >
                          <a href={event.link} target="_blank" rel="noopener noreferrer">
                            Get Tickets
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </a>
                        </Button>
                      )}
                      <Button
                        asChild
                        data-testid={`event-volunteer-btn-${event.id}`}
                        variant="outline"
                        className="border-2 border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white rounded-full px-6 transition-all duration-300"
                      >
                        <a href="#festival-volunteer">
                          Volunteer & Earn 2 Tickets
                        </a>
                      </Button>
                      <Button
                        asChild
                        variant="outline"
                        className="border-2 border-[#5D4E6D] text-[#5D4E6D] hover:bg-[#5D4E6D] hover:text-white rounded-full px-6 transition-all duration-300"
                      >
                        <Link to="/contact">Learn More</Link>
                      </Button>
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Festival Volunteer Section */}
      <section id="festival-volunteer" className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-72 h-72 bg-[#E07A5F]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#5D4E6D]/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            {/* Left: Perks */}
            <div className="lg:col-span-2">
              <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">
                Volunteer Opportunity
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4 mb-6 leading-tight">
                Be a Festival Volunteer
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Join the Aloha ohana on the ground at the ALOHA TEQUILA & ARTS Festival.
                Work just <span className="font-bold text-[#2D2A32]">2 hours</span> and you'll
                earn <span className="font-bold text-[#E07A5F]">2 free tickets</span> to the event.
              </p>

              <div className="space-y-5">
                <div className="flex items-start gap-4 p-5 bg-[#F5F0EB] rounded-2xl">
                  <div className="w-12 h-12 bg-[#E07A5F] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Ticket className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#2D2A32] mb-1">2 Free Festival Tickets</h4>
                    <p className="text-gray-600 text-sm">
                      Earn two complimentary tickets to enjoy the festival on your time off.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 bg-[#F5F0EB] rounded-2xl">
                  <div className="w-12 h-12 bg-[#5D4E6D] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#2D2A32] mb-1">Just 2 Hours of Work</h4>
                    <p className="text-gray-600 text-sm">
                      Choose a 2-hour shift that works best for you on the day of the event.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 bg-[#F5F0EB] rounded-2xl">
                  <div className="w-12 h-12 bg-[#E07A5F] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#2D2A32] mb-1">Be Part of the Aloha Ohana</h4>
                    <p className="text-gray-600 text-sm">
                      Support cancer patients & families in Hawaiʻi while celebrating with the community.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              <Card className="border-0 shadow-2xl overflow-hidden">
                <div className="h-3 bg-gradient-to-r from-[#5D4E6D] to-[#E07A5F]" />
                <CardContent className="p-8 sm:p-10">
                  <h3 className="text-2xl font-bold text-[#2D2A32] mb-2">Sign Up to Volunteer</h3>
                  <p className="text-gray-500 text-sm mb-8">
                    Saturday, June 20th, 2026 · International Market Place, Waikiki
                  </p>

                  <form onSubmit={handleFestivalVolunteerSubmit} className="space-y-6" data-testid="festival-volunteer-form">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="fv-name">Full Name *</Label>
                        <Input
                          id="fv-name"
                          required
                          data-testid="festival-volunteer-name"
                          value={festivalVolunteer.name}
                          onChange={(e) => setFestivalVolunteer({ ...festivalVolunteer, name: e.target.value })}
                          className="mt-2 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label htmlFor="fv-email">Email *</Label>
                        <Input
                          id="fv-email"
                          type="email"
                          required
                          data-testid="festival-volunteer-email"
                          value={festivalVolunteer.email}
                          onChange={(e) => setFestivalVolunteer({ ...festivalVolunteer, email: e.target.value })}
                          className="mt-2 rounded-xl"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="fv-phone">Phone Number *</Label>
                        <Input
                          id="fv-phone"
                          type="tel"
                          required
                          data-testid="festival-volunteer-phone"
                          value={festivalVolunteer.phone}
                          onChange={(e) => setFestivalVolunteer({ ...festivalVolunteer, phone: e.target.value })}
                          className="mt-2 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label htmlFor="fv-tshirt">T-Shirt Size</Label>
                        <select
                          id="fv-tshirt"
                          data-testid="festival-volunteer-tshirt"
                          value={festivalVolunteer.tshirtSize}
                          onChange={(e) => setFestivalVolunteer({ ...festivalVolunteer, tshirtSize: e.target.value })}
                          className="mt-2 w-full rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm focus:border-[#5D4E6D] focus:outline-none focus:ring-2 focus:ring-[#5D4E6D]/20 h-10"
                        >
                          <option value="">Select size</option>
                          <option value="S">Small</option>
                          <option value="M">Medium</option>
                          <option value="L">Large</option>
                          <option value="XL">X-Large</option>
                          <option value="XXL">XX-Large</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="fv-shift">Preferred 2-Hour Shift *</Label>
                      <select
                        id="fv-shift"
                        required
                        data-testid="festival-volunteer-shift"
                        value={festivalVolunteer.shift}
                        onChange={(e) => setFestivalVolunteer({ ...festivalVolunteer, shift: e.target.value })}
                        className="mt-2 w-full rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm focus:border-[#5D4E6D] focus:outline-none focus:ring-2 focus:ring-[#5D4E6D]/20 h-10"
                      >
                        <option value="">Choose a shift</option>
                        {shiftOptions.map((shift) => (
                          <option key={shift} value={shift}>{shift}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="fv-notes">Notes (optional)</Label>
                      <Textarea
                        id="fv-notes"
                        rows={3}
                        placeholder="Any prior experience, dietary restrictions, or questions?"
                        data-testid="festival-volunteer-notes"
                        value={festivalVolunteer.notes}
                        onChange={(e) => setFestivalVolunteer({ ...festivalVolunteer, notes: e.target.value })}
                        className="mt-2 rounded-xl resize-none"
                      />
                    </div>

                    <label className="flex items-start gap-3 p-4 bg-[#F5F0EB] rounded-xl cursor-pointer">
                      <input
                        type="checkbox"
                        required
                        data-testid="festival-volunteer-agreement"
                        checked={festivalVolunteer.agreement}
                        onChange={(e) => setFestivalVolunteer({ ...festivalVolunteer, agreement: e.target.checked })}
                        className="mt-1 w-5 h-5 accent-[#E07A5F] flex-shrink-0"
                      />
                      <span className="text-sm text-gray-700">
                        I confirm I can work my full <strong>2-hour shift</strong> at the festival and
                        I'm 21+ years of age. I understand that completing my shift earns me
                        <strong> 2 free festival tickets</strong>.
                      </span>
                    </label>

                    <Button
                      type="submit"
                      size="lg"
                      data-testid="festival-volunteer-submit"
                      className="w-full bg-[#E07A5F] hover:bg-[#C96A50] text-white rounded-full py-6 text-base font-semibold transition-all duration-300"
                    >
                      Submit Volunteer Application
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Past Events */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Past Success</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4">
              Recent Events
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Thank you to everyone who made these events possible!
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pastEvents.map((event) => (
              <Card
                key={event.id}
                className="border-0 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="h-2 bg-gradient-to-r from-[#5D4E6D] to-[#E07A5F]" />
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#2D2A32] mb-2">{event.title}</h3>
                  <div className="flex items-center text-gray-600 text-sm mb-2">
                    <Calendar className="w-4 h-4 mr-2" />
                    {event.date}
                  </div>
                  <div className="flex items-center text-gray-600 text-sm mb-4">
                    <MapPin className="w-4 h-4 mr-2" />
                    {event.location}
                  </div>
                  <div className="flex justify-between pt-4 border-t border-gray-100">
                    <div className="text-center">
                      <div className="flex items-center text-[#5D4E6D]">
                        <Users className="w-4 h-4 mr-1" />
                        <span className="font-bold">{event.attendees}</span>
                      </div>
                      <p className="text-xs text-gray-500">Attendees</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-[#E07A5F]">{event.raised}</p>
                      <p className="text-xs text-gray-500">Raised</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Host an Event */}
      <section className="py-24 bg-[#5D4E6D]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Want to Host a Fundraiser?
          </h2>
          <p className="text-gray-300 text-lg mb-10">
            Partner with us to organize a fundraising event in your community. 
            Together, we can make a bigger impact.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-[#E07A5F] hover:bg-[#C96A50] text-white rounded-full px-10 transition-all duration-300"
          >
            <Link to="/contact">
              Contact Us
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
